class DripGallery extends HTMLElement {
  connectedCallback() {
    // Seed from the server-rendered display:none state (see product-media-twe104.liquid) so the
    // redundant filterByIndex() call custom.js fires on page load — with the same indexes the
    // server already applied — is recognised as a no-op instead of tearing down and rebuilding
    // all 4 Swiper instances a second time immediately after _waitForLibs()'s own first init.
    this._hiddenIndexes = Array.from(this._qa('.TWE-104-content-carousel-slider-item'))
      .map((el, i) => (el.style.display === 'none' ? i : -1))
      .filter((i) => i !== -1);
    this._filterHandler = (e) => {
      const { hiddenIndexes } = e.detail || {};
      Array.isArray(hiddenIndexes) ? this.filterByIndex(hiddenIndexes) : this.clearFilter();
    };
    this.addEventListener('drip-gallery:filter', this._filterHandler);
    this._waitForLibs();
  }

  disconnectedCallback() {
    this.removeEventListener('drip-gallery:filter', this._filterHandler);
    if (this._pdpZoomSlider) this._pdpZoomSlider.destroy(true, true);
    if (this._pdpZoomSliderThumb) this._pdpZoomSliderThumb.destroy(true, true);
    if (this._pdpSlider) this._pdpSlider.destroy(true, true);
    if (this._pdpSliderThumb) this._pdpSliderThumb.destroy(true, true);
    if (this._photoswipeLightbox) this._photoswipeLightbox.destroy();
    if (this._clickHandler) document.body.removeEventListener('click', this._clickHandler);
  }

  // Hides slides at given indexes in all swipers and the desktop gallery, then reinitialises.
  // No-ops when the requested set matches the current one — avoids an expensive Swiper
  // teardown/rebuild across ~100 slides x 4 swipers for a call that would change nothing.
  filterByIndex(hiddenIndexes) {
    const next = hiddenIndexes.slice().sort((a, b) => a - b);
    const current = this._hiddenIndexes.slice().sort((a, b) => a - b);
    if (next.length === current.length && next.every((v, i) => v === current[i])) {
      return;
    }
    this._hiddenIndexes = next;
    this._applyHiddenState();
    this._reinitSwipers();
  }

  // Removes all index-based hiding and reinitialises.
  clearFilter() {
    this._hiddenIndexes = [];
    this._clearHiddenState();
    this._reinitSwipers();
  }

  // Applies display:none to the slides at this._hiddenIndexes.
  _applyHiddenState() {
    this._clearHiddenState();
    this._hiddenIndexes.forEach((i) => {
      const carouselSlide = Array.from(this._qa('.TWE-104-content-carousel-slider-item'))[i];
      const carouselThumb = Array.from(this._qa('.TWE-104-content-carousel-thumbnail-item'))[i];
      const zoomSlide    = Array.from(this._qa('.TWE-104-content-zoom-slider-item'))[i];
      const zoomThumb    = Array.from(this._qa('.TWE-104-content-zoom-thumbnail-item'))[i];
      const galleryItem  = this._q(`.TWE-104-content-gallery-item img[data-index="${i}"]`)
                             ?.closest('.TWE-104-content-gallery-item');

      if (carouselSlide) carouselSlide.style.display = 'none';
      if (carouselThumb) carouselThumb.style.display = 'none';
      if (zoomSlide)     zoomSlide.style.display     = 'none';
      if (zoomThumb)     zoomThumb.style.display     = 'none';
      if (galleryItem)   galleryItem.style.display   = 'none';
    });
  }

  _clearHiddenState() {
    [
      '.TWE-104-content-carousel-slider-item',
      '.TWE-104-content-carousel-thumbnail-item',
      '.TWE-104-content-zoom-slider-item',
      '.TWE-104-content-zoom-thumbnail-item',
      '.TWE-104-content-gallery-item',
    ].forEach((sel) => {
      this._qa(sel).forEach((el) => el.style.removeProperty('display'));
    });
  }

  reinitSwiper() {
    this._reinitSwipers();
  }

  _reinitSwipers() {
    // Restore any previously stripped swiper-slide classes before reinit
    this._qa('[data-swiper-slide-stripped]').forEach(el => {
      el.classList.add('swiper-slide');
      el.removeAttribute('data-swiper-slide-stripped');
    });
    if (this._clickHandler) document.body.removeEventListener('click', this._clickHandler);
    // cleanStyles:false — with cleanStyles:true, Swiper strips ALL inline styles from every
    // .swiper-slide element (incl. our display:none from _applyHiddenState()), forcing a costly
    // full re-apply pass across ~100 elements x 4 swipers on every single filter/reinit call.
    // Leaving cleanStyles off preserves display:none across destroy, and any stale transform/width
    // styles Swiper leaves behind are immediately overwritten by the new instance's own init pass.
    if (this._pdpZoomSlider)     this._pdpZoomSlider.destroy(true, false);
    if (this._pdpZoomSliderThumb) this._pdpZoomSliderThumb.destroy(true, false);
    if (this._pdpSlider)         this._pdpSlider.destroy(true, false);
    if (this._pdpSliderThumb)    this._pdpSliderThumb.destroy(true, false);
    if (this._photoswipeLightbox) this._photoswipeLightbox.destroy();
    this._addBehavior();
  }

  _q(selector) {
    return this.querySelector(selector);
  }

  _qa(selector) {
    return this.querySelectorAll(selector);
  }

  _waitForLibs(elapsed = 0) {
    if (window.Swiper && window.PhotoSwipe && window.PhotoSwipeLightbox) {
      this._addBehavior();
    } else if (elapsed < 10000) {
      setTimeout(() => this._waitForLibs(elapsed + 25), 25);
    }
  }

  _setTooltipWidth() {
    let smallest = Infinity;
    this._qa('.TWE-104-content-gallery-item').forEach((item) => {
      const w = item.getBoundingClientRect().width;
      if (w < smallest) smallest = w;
    });
    document.documentElement.style.setProperty(
      '--tooltip-width',
      window.innerWidth > 1199
        ? `${smallest + 12 + 24}px`
        : `${smallest * 2}px`
    );
  }

  _addClickEvents() {
    this._clickHandler = (e) => {
      const tooltipBtn = e.target.closest(
        '.TWE-104-content-gallery-item-tooltip-header-button, .TWE-104-content-carousel-slider-item-tooltip-header-button'
      );
      const galleryImg  = e.target.closest('.TWE-104-content-gallery-item>img:not([alt="overlay"])');
      const zoomClose   = e.target.closest('.TWE-104-content-zoom-control-close');
      const zoomToggle  = e.target.closest('.TWE-104-content-zoom-control-zoom>span, .TWE-104-content-zoom-control-zoom>svg');
      const mobileZoomBtn = e.target.closest('.TWE-104-content-carousel-slider-zoom');



      if (tooltipBtn) {
        const base = tooltipBtn.closest(
          '.TWE-104-content-gallery-item-tooltip, .TWE-104-content-carousel-slider-item-tooltip'
        );
        this._setTooltipWidth();
        base.classList.toggle('active');
      } else if (galleryImg) {
        const index = parseInt(galleryImg.dataset.index);
        this._q('.TWE-104-content-zoom').classList.add('active');
        this._pdpZoomSlider.slideTo(index, 0);
      } else if (zoomClose) {
        this._q('.TWE-104-content-zoom').classList.remove('active');
        if (this._photoswipeLightbox.pswp) this._photoswipeLightbox.pswp.close();
      } else if (zoomToggle) {
      console.log("PhotoSwipe");
      console.log(this._photoswipeLightbox, "BOX");
      console.log(this._photoswipeLightbox.pswp, "PSPW");


        if (this._photoswipeLightbox.pswp) {
          this._photoswipeLightbox.pswp.close();
        } else {
          console.log(this._q('.TWE-104-content-zoom-slider-item.swiper-slide-active a'));
          this._q('.TWE-104-content-zoom-slider-item.swiper-slide-active a').click();
        }
      } else if (mobileZoomBtn) {
        const active = this._q('.TWE-104-content-carousel-slider-item.swiper-slide-active');
        this._q(`.TWE-104-content-gallery-item>[data-index="${active.dataset.index}"]`).click();
      }
    };
    document.body.addEventListener('click', this._clickHandler);
  }

  _addBehavior() {
    // Remove swiper-slide class from CSS-hidden slides so Swiper ignores them in index calculations.
    // Marked with data-swiper-slide-stripped so _reinitSwipers can restore before re-checking.
    this._qa('.swiper-slide').forEach(slide => {
      if (window.getComputedStyle(slide).display === 'none') {
        slide.classList.remove('swiper-slide');
        slide.setAttribute('data-swiper-slide-stripped', '');
      }
    });

    this._setTooltipWidth();
    this._addClickEvents();

    this._pdpZoomSliderThumb = new Swiper(this._q('.TWE-104-content-zoom-thumbnail'), {
      slidesPerView: 'auto',
      spaceBetween: 10,
      freeMode: true,
      watchSlidesProgress: true,
    });

    this._pdpZoomSlider = new Swiper(this._q('.TWE-104-content-zoom-slider'), {
      spaceBetween: 10,
      navigation: {
        nextEl: this._q('.TWE-104-content-zoom-control-navigation>.swiper-button-next'),
        prevEl: this._q('.TWE-104-content-zoom-control-navigation>.swiper-button-prev'),
      },
      thumbs: { swiper: this._pdpZoomSliderThumb },
      on: {
        activeIndexChange: (e) => {
          this._q('.TWE-104-content-zoom-control-number>.current').innerHTML = e.activeIndex + 1;
          this._pdpSlider.slideTo(e.activeIndex);
          this._pdpZoomSliderThumb.slideTo(e.activeIndex);
          if (this._photoswipeLightbox.pswp) this._photoswipeLightbox.pswp.close();
        },
      },
    });

    this._pdpSliderThumb = new Swiper(this._q('.TWE-104-content-carousel-thumbnail'), {
      slidesPerView: 'auto',
      spaceBetween: 8,
      freeMode: true,
      watchSlidesProgress: true,
    });

    this._pdpSlider = new Swiper(this._q('.TWE-104-content-carousel-slider'), {
      slidesPerView: 1.055,
      spaceBetween: 8,
      thumbs: { swiper: this._pdpSliderThumb },
      on: {
        activeIndexChange: (e) => {
          this._pdpSliderThumb.slideTo(e.activeIndex);
        },
      },
    });

    this._photoswipeLightbox = new PhotoSwipeLightbox({
      bgOpacity: 1,
      maxSpreadZoom: 1,
      initialZoomLevel: 'fill',
      tapAction: 'close',
      doubleTapAction: 'close',
      imageClickAction: 'close',
      showAnimationDuration: 250,
      hideAnimationDuration: 100,
      zoom: false,
      close: false,
      counter: false,
      arrowPrev: false,
      arrowNext: false,
      wheelToZoom: false,
      pinchToClose: false,
      closeOnVerticalDrag: false,
      pswpModule: window.PhotoSwipe,
      showHideAnimationType: 'zoom',
      gallery: this._q('.TWE-104-content-zoom-slider'),
      children: '.TWE-104-content-zoom-slider-item-wrapper',
    });

    this._photoswipeLightbox.init();
    this._photoswipeLightbox.on('change', () => {
      const { pswp } = this._photoswipeLightbox;
      this._pdpZoomSlider.slideTo(pswp.currIndex, 0, false);
    });
  }
}

customElements.define('drip-gallery', DripGallery);
